class BoxBuilder extends HTMLElement {
  constructor() {
    super();
    this.state = {};
    this.isAddingToCart = false;
    this.packSize = null;
    this.selectedPlanName = '';
    this.purchaseType = 'subscription';
    this.isMobileSidebarOpen = false;
    this.isSummaryExpanded = false;
    this.submitProgressState = 'idle';
    this.pendingPlanDefault = true;
    this.handleClick = this.handleClick.bind(this);
    this.handleChange = this.handleChange.bind(this);
    this.handleKeydown = this.handleKeydown.bind(this);
  }

  connectedCallback() {
    this.storageKey = this.dataset.storageKey || 'box-builder';
    this.packSizeStorageKey = `${this.storageKey}-pack-size`;
    this.planStorageKey = `${this.storageKey}-plan`;
    this.purchaseTypeStorageKey = `${this.storageKey}-purchase-type`;
    this.storageLeaseCookieKey = `${this.storageKey}-lease`;
    this.storageLeaseMaxAgeSeconds = 60 * 60 * 2;
    this.currencyCode = this.dataset.currencyCode || window.Shopify?.currency?.active || 'USD';
    this.discountsEnabled = this.dataset.discountsEnabled === 'true';
    this.availablePackSizes = this.getAvailablePackSizes();
    this.defaultPackSize = this.getDefaultPackSize();
    [
      ['cards', '[data-box-product]', true],
      ['selectedCount', '[data-selected-count]'],
      ['selectedItems', '[data-selected-items]'],
      ['selectedTotalCompare', '[data-selected-total-compare]'],
      ['selectedTotal', '[data-selected-total]'],
      ['mobileSelectedCount', '[data-mobile-selected-count]'],
      ['mobileSelectedTotal', '[data-mobile-selected-total]'],
      ['mobileProgressBanner', '[data-mobile-progress-banner]'],
      ['mobileProgressFill', '[data-mobile-progress-fill]'],
      ['mobileProgressMarkers', '[data-mobile-progress-markers]'],
      ['mobileProgressLabels', '[data-mobile-progress-labels]'],
      ['progressFill', '[data-progress-fill]'],
      ['progressMarkers', '[data-progress-markers]'],
      ['sidebarInner', '.box-builder__sidebar-inner'],
      ['emptyState', '[data-empty-state]'],
      ['hasItemsContent', '[data-has-items-content]', true],
      ['selectedItemsToggle', '[data-selected-items-toggle]'],
      ['packSizePromo', '[data-pack-size-promo]'],
      ['packSizeInputs', '[data-pack-size]', true],
      ['purchaseTypeInputs', '[data-purchase-type]', true],
      ['oneTimeTotal', '[data-one-time-total]'],
      ['oneTimeCompare', '[data-one-time-compare]'],
      ['subscriptionTotal', '[data-subscription-total]'],
      ['subscriptionCompare', '[data-subscription-compare]'],
      ['subscriptionMeta', '[data-subscription-meta]'],
      ['subscriptionSavings', '[data-subscription-savings]'],
      ['deliverySettings', '[data-delivery-settings]'],
      ['frequencySelect', '[data-box-frequency]'],
      ['discountSummary', '[data-discount-summary]'],
      ['discountSummaryLabel', '[data-discount-summary-label]'],
      ['discountSummaryAmount', '[data-discount-summary-amount]'],
      ['mobileToggleButton', '[data-open-mobile-sidebar]'],
      ['mobileSidebar', '[data-mobile-sidebar]'],
      ['mobileSidebarOverlay', '[data-mobile-sidebar-overlay]'],
      ['submitButton', '[data-add-selected-to-cart]'],
    ].forEach(([key, selector, all]) => {
      this[key] = all ? Array.from(this.querySelectorAll(selector)) : this.querySelector(selector);
    });
    this.submitButtonBaseText = this.submitButton?.textContent?.trim() || 'Add box to cart';
    this.feedback = this.querySelector('[data-add-to-cart-feedback]');

    this.syncStorageLease();
    this.state = this.loadState();
    this.packSize = this.loadPackSize();
    this.selectedPlanName = this.loadPlanName();
    this.purchaseType = this.loadPurchaseType();
    this.syncPackSizeInputs();
    this.syncPurchaseTypeInputs();
    this.cards.forEach((card) => this.syncCard(card));
    this.renderSummary();

    this.addEventListener('click', this.handleClick);
    this.addEventListener('change', this.handleChange);
    document.addEventListener('keydown', this.handleKeydown);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.handleClick);
    this.removeEventListener('change', this.handleChange);
    document.removeEventListener('keydown', this.handleKeydown);
  }

  handleClick(event) {
    const removeSelectedItem = event.target.closest('[data-remove-selected-item]');

    if (removeSelectedItem) {
      this.removeSelectedItem(removeSelectedItem.dataset.removeSelectedItem);
      return;
    }

    const openMobileSidebar = event.target.closest('[data-open-mobile-sidebar]');

    if (openMobileSidebar) {
      this.openMobileSidebar();
      return;
    }

    const closeMobileSidebar = event.target.closest('[data-close-mobile-sidebar], [data-mobile-sidebar-overlay]');

    if (closeMobileSidebar) {
      this.closeMobileSidebar();
      return;
    }

    if (this.isMobileSidebarOpen && this.mobileSidebar && event.target === this.mobileSidebar) {
      this.closeMobileSidebar();
      return;
    }

    const selectedItemsToggle = event.target.closest('[data-selected-items-toggle]');

    if (selectedItemsToggle) {
      this.isSummaryExpanded = !this.isSummaryExpanded;
      this.renderSummary();
      return;
    }

    const submitButton = event.target.closest('[data-add-selected-to-cart]');

    if (submitButton) {
      this.addSelectionsToCart();
      return;
    }

    const addButton = event.target.closest('[data-add-button]');

    if (!addButton) return;

    const card = addButton.closest('[data-box-product]');
    if (!card) return;

    this.updateQuantity(card, 1);
  }

  handleChange(event) {
    const packSizeInput = event.target.closest('[data-pack-size]');

    if (packSizeInput) {
      this.updatePackSize(packSizeInput.value);
      return;
    }

    const purchaseTypeInput = event.target.closest('[data-purchase-type]');

    if (purchaseTypeInput) {
      this.updatePurchaseType(purchaseTypeInput.value);
      return;
    }

    const frequencySelect = event.target.closest('[data-box-frequency]');

    if (frequencySelect) {
      this.updatePlanName(frequencySelect.value);
      return;
    }

    const input = event.target.closest('[data-quantity-input]');

    if (!input) return;

    const card = input.closest('[data-box-product]');
    if (!card) return;

    this.updateQuantity(card, input.value);
  }

  handleKeydown(event) {
    if (event.key === 'Escape' && this.isMobileSidebarOpen) {
      this.closeMobileSidebar();
    }
  }

  getStorageKeys() {
    return [this.storageKey, this.packSizeStorageKey, this.planStorageKey, this.purchaseTypeStorageKey];
  }

  hasStorageLease() {
    return document.cookie
      .split(';')
      .map((cookie) => cookie.trim())
      .some((cookie) => cookie.startsWith(`${encodeURIComponent(this.storageLeaseCookieKey)}=`));
  }

  refreshStorageLease() {
    const expiresAt = new Date(Date.now() + this.storageLeaseMaxAgeSeconds * 1000).toUTCString();
    document.cookie = `${encodeURIComponent(this.storageLeaseCookieKey)}=active; max-age=${this.storageLeaseMaxAgeSeconds}; expires=${expiresAt}; path=/; SameSite=Lax`;
  }

  clearStoredSession() {
    this.getStorageKeys().forEach((key) => this.removeStoredValue(key));

    document.cookie = `${encodeURIComponent(this.storageLeaseCookieKey)}=; max-age=0; path=/; SameSite=Lax`;
  }

  syncStorageLease() {
    if (this.hasStorageLease()) {
      return;
    }

    this.clearStoredSession();
  }

  getStoredValue(key, fallback = '') {
    try {
      return window.localStorage.getItem(key) ?? fallback;
    } catch (error) {
      return fallback;
    }
  }

  setStoredValue(key, value) {
    try {
      if (value === null || value === undefined || value === '') {
        window.localStorage.removeItem(key);
      } else {
        window.localStorage.setItem(key, String(value));
      }
      this.refreshStorageLease();
    } catch (error) {
      // Ignore storage failures so the UI still works.
    }
  }

  removeStoredValue(key) {
    try {
      window.localStorage.removeItem(key);
    } catch (error) {
      // Ignore storage failures so the UI still works.
    }
  }

  loadState() {
    try {
      const storedState = this.getStoredValue(this.storageKey);
      const parsedState = storedState ? JSON.parse(storedState) : {};

      return parsedState && typeof parsedState === 'object' && !Array.isArray(parsedState) ? parsedState : {};
    } catch (error) {
      return {};
    }
  }

  saveState() {
    this.setStoredValue(this.storageKey, JSON.stringify(this.state));
  }

  getAvailablePackSizes() {
    const parsedSizes = String(this.dataset.packSizes || '')
      .split(',')
      .map((value) => Number.parseInt(value.trim(), 10))
      .filter((value, index, values) => Number.isFinite(value) && value > 0 && values.indexOf(value) === index);

    if (!parsedSizes.length) {
      return [1, 2, 3, 4];
    }

    if (!parsedSizes.includes(1)) {
      parsedSizes.unshift(1);
    }

    return parsedSizes;
  }

  getDefaultPackSize() {
    const parsedDefault = Number.parseInt(this.dataset.defaultPackSize || '', 10);

    if (this.availablePackSizes.includes(parsedDefault)) {
      return parsedDefault;
    }

    return this.availablePackSizes[0] || 1;
  }

  getSortedPackSizes() {
    return [...this.availablePackSizes].sort((a, b) => a - b);
  }

  getMaxPackSize() {
    const sortedPackSizes = this.getSortedPackSizes();
    return sortedPackSizes[sortedPackSizes.length - 1] || this.defaultPackSize;
  }

  getMinPackSize() {
    return this.getSortedPackSizes()[0] || this.defaultPackSize;
  }

  getPackSizeForSelectedCount(totalSelected = this.getTotalSelectedCount()) {
    if (totalSelected <= 0) {
      return this.getMinPackSize();
    }

    const sortedPackSizes = this.getSortedPackSizes();
    return sortedPackSizes.find((packSize) => packSize >= totalSelected) || this.getMaxPackSize();
  }

  syncPackSizeWithSelection() {
    const nextPackSize = this.getPackSizeForSelectedCount();

    if (nextPackSize === this.packSize) {
      return false;
    }

    this.packSize = nextPackSize;
    this.pendingPlanDefault = true;
    this.savePackSize();
    return true;
  }

  loadPackSize() {
    const storedValue = Number(this.getStoredValue(this.packSizeStorageKey));
    return this.availablePackSizes.includes(storedValue) ? storedValue : this.defaultPackSize;
  }

  savePackSize() {
    this.setStoredValue(this.packSizeStorageKey, this.packSize);
  }

  loadPlanName() {
    return this.getStoredValue(this.planStorageKey);
  }

  savePlanName() {
    this.setStoredValue(this.planStorageKey, this.selectedPlanName);
  }

  loadPurchaseType() {
    return this.getStoredValue(this.purchaseTypeStorageKey) === 'one-time' ? 'one-time' : 'subscription';
  }

  savePurchaseType() {
    this.setStoredValue(this.purchaseTypeStorageKey, this.purchaseType);
  }

  getCardData(card) {
    return {
      id: card.dataset.productId,
      variantId: card.dataset.variantId,
      title: card.dataset.productTitle,
      basePrice: Number(card.dataset.productPrice || 0),
      url: card.dataset.productUrl || '',
      image: card.dataset.productImage || '',
      plans: this.getCardPlans(card),
    };
  }

  getCardPlans(card) {
    const plansData = card.querySelector('[data-product-plans]');

    if (!plansData) return {};

    try {
      const plans = JSON.parse(plansData.textContent.trim() || '[]');

      return plans.reduce((result, plan) => {
        if (!plan?.id || !plan?.name) return result;

        result[plan.name] = {
          id: String(plan.id),
          label: plan.name,
          price: Number(plan.price || 0),
        };

        return result;
      }, {});
    } catch (error) {
      return {};
    }
  }

  normalizeQuantity(quantity) {
    const parsedQuantity = Number.parseInt(quantity, 10);

    if (Number.isNaN(parsedQuantity) || parsedQuantity < 0) {
      return 0;
    }

    return parsedQuantity;
  }

  getItemCount(items) {
    return Object.values(items || {}).reduce((count, item) => count + Number(item?.quantity || 0), 0);
  }

  getTotalSelectedCount() {
    return this.getItemCount(this.state);
  }

  getMaxQuantityForCard(card) {
    const productId = card.dataset.productId;
    const currentQuantity = this.state[productId]?.quantity || 0;
    const totalExcludingCurrent = this.getTotalSelectedCount() - currentQuantity;

    return Math.max(this.getMaxPackSize() - totalExcludingCurrent, 0);
  }

  updateQuantity(card, quantity) {
    const normalizedQuantity = Math.min(this.normalizeQuantity(quantity), this.getMaxQuantityForCard(card));
    const cardData = this.getCardData(card);

    if (normalizedQuantity > 0) {
      this.state[cardData.id] = {
        ...cardData,
        quantity: normalizedQuantity,
      };
    } else {
      delete this.state[cardData.id];
    }

    this.saveState();
    this.setFeedback('');
    this.syncCard(card);
    this.renderSummary();
  }

  getSelectedItems() {
    return Object.values(this.state).filter((item) => item.variantId && item.quantity > 0);
  }

  getSelectedItemCount(items = this.getSelectedItems()) {
    return this.getItemCount(items);
  }

  getExpandedSummaryItems(items = this.getSelectedItems()) {
    return items.flatMap((item) =>
      Array.from({ length: Math.max(Number(item.quantity || 0), 0) }, () => ({
        ...item,
        quantity: 1,
      })),
    );
  }

  removeSelectedItem(productId) {
    if (!productId || !this.state[productId]) return;

    const nextQuantity = Math.max((this.state[productId]?.quantity || 0) - 1, 0);

    if (nextQuantity > 0) {
      this.state[productId] = {
        ...this.state[productId],
        quantity: nextQuantity,
      };
    } else {
      delete this.state[productId];
    }

    this.saveState();
    this.setFeedback('');

    const card = this.querySelector(`[data-box-product][data-product-id="${CSS.escape(productId)}"]`);

    if (card) {
      this.syncCard(card);
    }

    this.renderSummary();
  }

  syncCard(card) {
    const selectedItem = this.state[card.dataset.productId];
    const quantity = selectedItem?.quantity || 0;
    const boxIsFull = this.getTotalSelectedCount() >= this.getMaxPackSize();
    const maxQuantityForCard = this.getMaxQuantityForCard(card);
    const addButton = card.querySelector('[data-add-button]');
    const addButtonLabel = card.querySelector('[data-add-button-label]');
    const addButtonIcon = card.querySelector('[data-add-button-icon]');
    const quantityControl = card.querySelector('[data-quantity-control]');
    const quantityInput = card.querySelector('[data-quantity-input]');
    const plusButton = card.querySelector(".quantity__button[name='plus']");

    if (quantityInput) {
      quantityInput.value = quantity;
      quantityInput.max = maxQuantityForCard;
    }

    if (addButton) {
      addButton.hidden = quantity > 0;
      addButton.disabled = boxIsFull && quantity === 0;
    }

    if (addButtonLabel) {
      addButtonLabel.textContent = 'Add';
    }

    if (addButtonIcon) {
      addButtonIcon.hidden = false;
    }

    if (quantityControl) {
      quantityControl.hidden = quantity === 0;

      if (typeof quantityControl.validateQtyRules === 'function') {
        quantityControl.validateQtyRules();
      }
    }

    if (plusButton) {
      const disablePlus = quantity >= maxQuantityForCard || (boxIsFull && quantity > 0);
      plusButton.disabled = disablePlus;
      plusButton.classList.toggle('disabled', disablePlus);
    }

    if (selectedItem && quantity > 0) {
      this.state[card.dataset.productId] = {
        ...this.getCardData(card),
        quantity,
      };
    }
  }

  updatePlanName(planName) {
    this.selectedPlanName = planName;
    this.pendingPlanDefault = false;
    this.savePlanName();
    this.setFeedback('');
    this.renderSummary();
  }

  updatePackSize() {
    this.syncPackSizeWithSelection();
    this.syncPackSizeInputs();
    this.setFeedback('Pack size updates as you add or remove products.');
    this.renderSummary();
  }

  updatePurchaseType(purchaseType) {
    this.purchaseType = purchaseType === 'subscription' ? 'subscription' : 'one-time';
    if (this.isSubscription()) {
      this.pendingPlanDefault = true;
    }
    this.savePurchaseType();
    this.syncPurchaseTypeInputs();
    this.setFeedback('');
    this.renderSummary();
  }

  syncPurchaseTypeInputs() {
    this.purchaseTypeInputs.forEach((input) => {
      input.checked = input.value === this.purchaseType;
    });

    this.syncPackSizeDiscountLabels();

    if (this.sidebarInner) {
      this.sidebarInner.classList.toggle('is-subscription', this.isSubscription());
      this.sidebarInner.classList.toggle('is-one-time', !this.isSubscription());
    }
  }

  syncPackSizeDiscountLabels() {
    this.packSizeInputs.forEach((input) => {
      const option = input.closest('.box-builder__pack-size-option');
      const discountLabel = option?.querySelector('[data-pack-size-discount-label]');

      if (!discountLabel) return;

      const discountPercentage = this.getDiscountPercentageForSelection(input.value, this.purchaseType);

      if (discountPercentage) {
        discountLabel.textContent = `${discountPercentage}%`;
        discountLabel.hidden = false;
      } else {
        discountLabel.textContent = '';
        discountLabel.hidden = true;
      }
    });
  }

  syncPackSizeInputs() {
    const totalSelected = this.getTotalSelectedCount();

    this.packSizeInputs.forEach((input) => {
      const packSizeValue = Number(input.value);
      const isDisabled = packSizeValue < totalSelected;
      const overBy = Math.max(totalSelected - packSizeValue, 0);
      const option = input.closest('.box-builder__pack-size-option');
      const disabledReason = isDisabled
        ? `Lower your product count by ${overBy} to use this pack size.`
        : 'Pack size updates as you add or remove products.';

      input.checked = packSizeValue === this.packSize;
      input.disabled = true;
      option?.classList.toggle('is-disabled', isDisabled);

      if (option) {
        option.dataset.disabledReason = disabledReason;
        if (disabledReason) {
          option.setAttribute('title', disabledReason);
        } else {
          option.removeAttribute('title');
        }
      }
    });
  }

  getNextDiscountPackSize(totalSelected = this.getTotalSelectedCount()) {
    const currentDiscountPercentage = this.getCurrentDiscountPercentage();

    return this.getSortedPackSizes().find((packSize) => {
      if (packSize <= totalSelected) return false;

      return this.getDiscountPercentageForSelection(packSize, this.purchaseType) > currentDiscountPercentage;
    });
  }

  getHighestDiscountPercentage() {
    return this.getSortedPackSizes().reduce((highestDiscountPercentage, packSize) => {
      return Math.max(highestDiscountPercentage, this.getDiscountPercentageForSelection(packSize, this.purchaseType));
    }, 0);
  }

  updatePackSizePromo(totalSelected = this.getTotalSelectedCount()) {
    if (!this.packSizePromo) return;

    if (totalSelected <= 0) {
      this.setText(this.packSizePromo, 'Add minimum 1 product to continue.');
      this.setHidden(this.packSizePromo, false);
      return;
    }

    const nextPackSize = this.getNextDiscountPackSize(totalSelected);

    if (!nextPackSize) {
      const currentDiscountPercentage = this.getCurrentDiscountPercentage();
      const highestDiscountPercentage = this.getHighestDiscountPercentage();

      if (highestDiscountPercentage > 0 && currentDiscountPercentage >= highestDiscountPercentage) {
        this.setText(this.packSizePromo, `You have unlocked the highest discount of ${highestDiscountPercentage}%.`);
        this.setHidden(this.packSizePromo, false);
        return;
      }

      this.setText(this.packSizePromo);
      this.setHidden(this.packSizePromo);
      return;
    }

    const itemsNeeded = Math.max(nextPackSize - totalSelected, 0);
    const itemLabel = itemsNeeded === 1 ? 'item' : 'items';
    const discountPercentage = this.getDiscountPercentageForSelection(nextPackSize, this.purchaseType);
    const leadingCopy = totalSelected > 0 ? `Add ${itemsNeeded} more` : `Add ${itemsNeeded}`;

    this.setText(this.packSizePromo, `${leadingCopy} ${itemLabel} to unlock a ${discountPercentage}% discount.`);
    this.setHidden(this.packSizePromo, false);
  }

  isSubscription() {
    return this.purchaseType === 'subscription';
  }

  openMobileSidebar() {
    if (!this.mobileSidebar || !this.mobileSidebarOverlay) return;

    this.isMobileSidebarOpen = true;
    this.classList.add('is-mobile-sidebar-open');
    this.mobileSidebar.classList.add('is-mobile-open');
    this.mobileSidebarOverlay.hidden = false;
    if (this.mobileToggleButton) {
      this.mobileToggleButton.setAttribute('aria-expanded', 'true');
    }
    document.body.classList.add('overflow-hidden');
  }

  closeMobileSidebar() {
    if (!this.mobileSidebar || !this.mobileSidebarOverlay) return;

    this.isMobileSidebarOpen = false;
    this.classList.remove('is-mobile-sidebar-open');
    this.mobileSidebar.classList.remove('is-mobile-open');
    this.mobileSidebarOverlay.hidden = true;
    if (this.mobileToggleButton) {
      this.mobileToggleButton.setAttribute('aria-expanded', 'false');
    }
    document.body.classList.remove('overflow-hidden');
  }

  getCommonPlanNames(items) {
    if (!items.length) return [];

    return Object.keys(items[0].plans || {}).filter((planName) => items.every((item) => item.plans?.[planName]?.id));
  }

  getPreviewPlanName(commonPlanNames) {
    if (!commonPlanNames.length) return '';
    if (commonPlanNames.includes(this.selectedPlanName)) return this.selectedPlanName;
    return commonPlanNames[0];
  }

  getPreferredPlanName(commonPlanNames) {
    if (!commonPlanNames.length) return '';

    const preferredKeyword = this.packSize === 1 ? '30' : this.packSize === 2 ? '60' : '90';
    return commonPlanNames.find((planName) => String(planName).includes(preferredKeyword)) || '';
  }

  getDefaultPlanDisplayLabel() {
    return this.packSize === 1 ? '30 days' : this.packSize === 2 ? '60 days' : '90 days';
  }

  getPlanDisplayLabel(planName) {
    const normalizedPlanName = String(planName || '').trim();

    if (!normalizedPlanName) {
      return '';
    }

    return normalizedPlanName.replace(/^deliver every\s+/i, '');
  }

  getOneTimeTotal(items) {
    return items.reduce((total, item) => total + (item.basePrice ?? 0) * item.quantity, 0);
  }

  getSubscriptionTotal(items, planName) {
    if (!planName) return 0;

    return items.reduce(
      (total, item) => total + (item.plans?.[planName]?.price ?? item.basePrice ?? 0) * item.quantity,
      0,
    );
  }

  getSubscriptionSavingsPercentage(items, planName) {
    const oneTimeTotal = this.getOneTimeTotal(items);
    const subscriptionTotal = this.getSubscriptionTotal(items, planName);

    if (!planName || oneTimeTotal <= 0 || subscriptionTotal <= 0 || subscriptionTotal >= oneTimeTotal) {
      return 0;
    }

    return Math.round(((oneTimeTotal - subscriptionTotal) / oneTimeTotal) * 100);
  }

  updatePurchaseTypeSummary(selectedItems, commonPlanNames) {
    const oneTimeTotal = this.getOneTimeTotal(selectedItems);
    const oneTimeDiscountMapping = this.getDiscountMappingForSelection(this.packSize, 'one-time');
    const oneTimeDiscountAmount = this.getDiscountAmount(selectedItems, oneTimeDiscountMapping);
    const discountedOneTimeTotal = Math.max(oneTimeTotal - oneTimeDiscountAmount, 0);
    const hasOneTimeSavings = Boolean(oneTimeDiscountMapping) && oneTimeDiscountAmount > 0;
    const previewPlanName = this.getPreviewPlanName(commonPlanNames);
    const subscriptionTotal = this.getSubscriptionTotal(selectedItems, previewPlanName);
    const subscriptionDiscountMapping = this.getDiscountMappingForSelection(this.packSize, 'subscription');
    const totalItems = this.getSelectedItemCount(selectedItems);
    const subscriptionDiscountAmount =
      totalItems >= this.packSize
        ? this.getDiscountAmount(selectedItems, subscriptionDiscountMapping, previewPlanName)
        : 0;
    const discountedSubscriptionTotal = Math.max(subscriptionTotal - subscriptionDiscountAmount, 0);
    const showPurchaseTypePrices = totalItems >= this.packSize;
    const subscriptionInput = this.purchaseTypeInputs.find((input) => input.value === 'subscription');
    const hasSubscriptionSavings =
      Boolean(previewPlanName) && oneTimeTotal > 0 && subscriptionTotal > 0 && subscriptionTotal < oneTimeTotal;

    this.setText(this.oneTimeTotal, this.formatMoney(hasOneTimeSavings ? discountedOneTimeTotal : oneTimeTotal));
    this.setHidden(this.oneTimeTotal, !showPurchaseTypePrices);
    this.setText(this.oneTimeCompare, this.formatMoney(oneTimeTotal));
    this.toggleHidden(this.oneTimeCompare, !showPurchaseTypePrices || !hasOneTimeSavings);
    this.setText(
      this.subscriptionTotal,
      previewPlanName
        ? this.formatMoney(subscriptionDiscountAmount > 0 ? discountedSubscriptionTotal : subscriptionTotal)
        : this.formatMoney(0),
    );
    this.setHidden(this.subscriptionTotal, !showPurchaseTypePrices);

    if (this.subscriptionMeta) {
      const showSubscriptionMeta = this.isSubscription();

      if (!showSubscriptionMeta) {
        this.setText(this.subscriptionMeta);
      } else if (!selectedItems.length) {
        this.setText(this.subscriptionMeta, 'Choose products to preview subscription pricing');
      } else if (!previewPlanName) {
        this.setText(this.subscriptionMeta, 'Subscription unavailable for this selection');
      } else {
        this.setText(this.subscriptionMeta, previewPlanName);
      }

      this.toggleHidden(this.subscriptionMeta, !showSubscriptionMeta);
    }

    this.setText(this.subscriptionCompare, this.formatMoney(oneTimeTotal));
    this.toggleHidden(this.subscriptionCompare, !showPurchaseTypePrices || !hasSubscriptionSavings);
    this.setText(this.subscriptionSavings);
    this.setHidden(this.subscriptionSavings);

    if (subscriptionInput) {
      subscriptionInput.disabled = selectedItems.length > 0 && !previewPlanName;

      if (subscriptionInput.disabled && this.isSubscription()) {
        this.purchaseType = 'one-time';
        this.savePurchaseType();
        this.syncPurchaseTypeInputs();
      }
    }
  }

  updatePlanOptions(selectedItems) {
    if (!this.frequencySelect || !this.deliverySettings) return [];

    const commonPlanNames = this.getCommonPlanNames(selectedItems);
    const showSubscriptionPlans = this.isSubscription();

    this.deliverySettings.hidden = !showSubscriptionPlans;

    if (!showSubscriptionPlans) {
      this.setDisabled(this.frequencySelect);
      this.frequencySelect.innerHTML = '<option value="">Select delivery option</option>';
      return commonPlanNames;
    }

    if (!commonPlanNames.length) {
      this.selectedPlanName = '';
      this.setDisabled(this.frequencySelect);
      this.frequencySelect.innerHTML = `<option value="">${this.getDefaultPlanDisplayLabel()}</option>`;
      this.savePlanName();
      return commonPlanNames;
    }

    this.frequencySelect.innerHTML = '';

    commonPlanNames.forEach((planName) => {
      const option = document.createElement('option');
      option.value = planName;
      option.textContent = this.getPlanDisplayLabel(planName);
      this.frequencySelect.appendChild(option);
    });

    const preferredPlanName = this.getPreferredPlanName(commonPlanNames);

    if (this.pendingPlanDefault && preferredPlanName) {
      this.selectedPlanName = preferredPlanName;
      this.pendingPlanDefault = false;
      this.savePlanName();
    } else if (!commonPlanNames.includes(this.selectedPlanName)) {
      this.selectedPlanName = preferredPlanName || commonPlanNames[0];
      this.pendingPlanDefault = false;
      this.savePlanName();
    }

    this.setDisabled(this.frequencySelect, false);
    this.frequencySelect.value = this.selectedPlanName;

    return commonPlanNames;
  }

  updateSubmitState(selectedItems = this.getSelectedItems()) {
    if (!this.submitButton) return;

    const totalItems = this.getSelectedItemCount(selectedItems);
    const hasItems = selectedItems.length > 0;
    const packSizeReached = totalItems >= this.packSize;
    const missingSubscriptionPlan = this.isSubscription() && !this.selectedPlanName;
    const remainingItems = Math.max(this.packSize - totalItems, 0);
    const totalPrice = packSizeReached ? this.getCalculatedTotalPrice(selectedItems) : 0;

    this.setDisabled(
      this.submitButton,
      this.isAddingToCart || !hasItems || !packSizeReached || missingSubscriptionPlan,
    );

    if (this.isAddingToCart) {
      if (this.submitProgressState === 'discount') {
        this.setText(this.submitButton, 'Applying discount...');
      } else if (this.submitProgressState === 'updating-cart') {
        this.setText(this.submitButton, 'Updating cart...');
      } else if (this.submitProgressState === 'adding-items') {
        this.setText(this.submitButton, `Adding ${totalItems} products...`);
      } else {
        this.setText(this.submitButton, `Preparing ${this.packSize}-pack...`);
      }
      return;
    }

    if (!hasItems) {
      this.setText(this.submitButton, 'Add minimum 1 product to continue');
      return;
    }

    if (!packSizeReached) {
      this.setText(this.submitButton, `Add ${remainingItems} more`);
      return;
    }

    this.setText(this.submitButton, `${this.submitButtonBaseText} - ${this.formatMoney(totalPrice)}`);
  }

  getEffectiveItemPrice(item) {
    if (!this.isSubscription()) {
      return item.basePrice ?? 0;
    }

    return item.plans?.[this.selectedPlanName]?.price ?? item.basePrice ?? 0;
  }

  getSelectedTotal(items = this.getSelectedItems()) {
    return items.reduce((total, item) => total + this.getEffectiveItemPrice(item) * item.quantity, 0);
  }

  getCalculatedTotalPrice(items = this.getSelectedItems()) {
    const subtotalPrice = this.getSelectedTotal(items);
    const totalItems = this.getSelectedItemCount(items);
    const isDiscountEligible = totalItems >= this.packSize;

    if (!isDiscountEligible) {
      return subtotalPrice;
    }

    const previewPlanName = this.getPreviewPlanName(this.getCommonPlanNames(items));
    const discountMapping = this.getCurrentDiscountMapping();
    const discountAmount = this.getDiscountAmount(items, discountMapping, previewPlanName);

    return Math.max(subtotalPrice - discountAmount, 0);
  }

  getResolvedDiscountPercentage(discountMapping) {
    const discountPercentage = Number(discountMapping?.discount || 0);
    const displayDiscountPercentage = Number(discountMapping?.displayDiscount || 0);

    if (!discountPercentage && !displayDiscountPercentage) {
      return 0;
    }

    if (discountMapping?.purchaseType !== 'subscription') {
      return displayDiscountPercentage || discountPercentage;
    }

    return displayDiscountPercentage || this.getBaseSubscriptionDiscountPercentage() + discountPercentage;
  }

  getDiscountSummaryPercentage(discountMapping) {
    if (!discountMapping) {
      return 0;
    }

    return Number(discountMapping.summaryDiscount || discountMapping.displayDiscount || discountMapping.discount || 0);
  }

  getBaseSubscriptionDiscountPercentage() {
    return 15;
  }

  getAdditionalDiscountPercentage(discountMapping) {
    const discountPercentage = Number(discountMapping?.discount || 0);

    if (!discountPercentage) {
      return 0;
    }

    return discountPercentage;
  }

  getDiscountAmount(items, discountMapping, planName = this.selectedPlanName) {
    const discountPercentage = this.getAdditionalDiscountPercentage(discountMapping);

    if (!discountPercentage || !Array.isArray(items) || !items.length) {
      return 0;
    }

    return items.reduce((total, item) => {
      const unitPrice =
        discountMapping?.purchaseType === 'subscription'
          ? Number(item?.plans?.[planName]?.price ?? item?.basePrice ?? 0)
          : Number(item?.basePrice || 0);
      const quantity = Number(item?.quantity || 0);

      if (unitPrice <= 0 || quantity <= 0) {
        return total;
      }

      const lineDiscount = Math.floor(unitPrice * (discountPercentage / 100)) * quantity;
      return total + lineDiscount;
    }, 0);
  }

  getDiscountMappings() {
    if (!this.discountsEnabled) {
      return [];
    }

    return [
      { packSize: 2, purchaseType: 'one-time', code: 'BYOB2OT', discount: 10, displayDiscount: 10 },
      { packSize: 3, purchaseType: 'one-time', code: 'BYOB3OT', discount: 15, displayDiscount: 15 },
      { packSize: 4, purchaseType: 'one-time', code: 'BYOB4OT', discount: 20, displayDiscount: 20 },
      { packSize: 1, purchaseType: 'subscription', code: '', discount: 0, displayDiscount: 15, summaryDiscount: 15 },
      {
        packSize: 2,
        purchaseType: 'subscription',
        code: 'BYOB2SUB',
        discount: 5.88,
        displayDiscount: 20,
        summaryDiscount: 5,
      },
      {
        packSize: 3,
        purchaseType: 'subscription',
        code: 'BYOB3SUB',
        discount: 11.76,
        displayDiscount: 25,
        summaryDiscount: 10,
      },
      {
        packSize: 4,
        purchaseType: 'subscription',
        code: 'BYOB4SUB',
        discount: 17.65,
        displayDiscount: 30,
        summaryDiscount: 15,
      },
    ];
  }

  getLineItemProperties() {
    const bundleUdid = String(Math.floor(Math.random() * 10000)).padStart(4, '0');

    return {
      bundle: `BYOB ${this.packSize} pack - ${this.isSubscription() ? 'subscription' : 'one-time'} - ${bundleUdid}`,
    };
  }

  getDiscountMappingForSelection(packSize = this.packSize, purchaseType = this.purchaseType) {
    const normalizedPackSize = Number(packSize);
    const normalizedPurchaseType = purchaseType === 'subscription' ? 'subscription' : 'one-time';

    return (
      this.getDiscountMappings().find(
        (mapping) => mapping.packSize === normalizedPackSize && mapping.purchaseType === normalizedPurchaseType,
      ) || null
    );
  }

  getDiscountMappingForBundle(bundleValue = '') {
    const normalizedBundleValue = String(bundleValue || '').trim();
    const bundleMatch = normalizedBundleValue.match(/^BYOB\s+(\d+)\s+pack\s+-\s+(subscription|one-time)\s+-/i);

    if (!bundleMatch) {
      return null;
    }

    return this.getDiscountMappingForSelection(Number(bundleMatch[1]), bundleMatch[2].toLowerCase());
  }

  getCurrentDiscountMapping() {
    return this.getDiscountMappingForSelection(this.packSize, this.purchaseType);
  }

  getDiscountPercentageForSelection(packSize = this.packSize, purchaseType = this.purchaseType) {
    return this.getResolvedDiscountPercentage(this.getDiscountMappingForSelection(packSize, purchaseType));
  }

  getCurrentDiscountPercentage() {
    return this.getDiscountPercentageForSelection(this.packSize, this.purchaseType);
  }

  getDiscountCodeForBundle(bundleValue = '') {
    return this.getDiscountMappingForBundle(bundleValue)?.code || '';
  }

  async primeDiscountCode(discountCode) {
    const normalizedDiscountCode = String(discountCode || '').trim();
    if (!normalizedDiscountCode) return;

    await fetch(`/discount/${encodeURIComponent(normalizedDiscountCode)}`, {
      method: 'GET',
      credentials: 'same-origin',
      redirect: 'manual',
      mode: 'no-cors',
    });
  }

  getFallbackPlan(item) {
    const plans = Object.values(item?.plans || {});
    return plans.length ? plans[0] : null;
  }

  getCardDisplayPrice(card) {
    const item = this.getCardData(card);

    return item.basePrice ?? 0;
  }

  updateCardPrice(card) {
    const priceDisplay = card.querySelector('[data-product-price-display]');

    if (!priceDisplay) return;

    priceDisplay.textContent = this.formatMoney(this.getCardDisplayPrice(card));
  }

  updateCardPrices() {
    this.cards.forEach((card) => this.updateCardPrice(card));
  }

  syncCards() {
    this.cards.forEach((card) => this.syncCard(card));
  }

  setText(element, text = '') {
    if (element) element.textContent = text;
  }
  setHidden(element, hidden = true) {
    if (element) element.hidden = hidden;
  }
  toggleHidden(element, hidden = true) {
    if (element) element.toggleAttribute('hidden', hidden);
  }
  setDisabled(element, disabled = true) {
    if (element) element.disabled = disabled;
  }

  setFeedback(message = '', isError = false) {
    if (!this.feedback) return;

    this.setText(this.feedback, message);
    this.feedback.classList.toggle('box-builder__feedback--error', isError);
  }

  updateCartBubble(sectionHtml) {
    const cartBubble = document.getElementById('cart-icon-bubble');

    if (!cartBubble || !sectionHtml) return;

    const parsedDocument = new DOMParser().parseFromString(sectionHtml, 'text/html');
    const updatedBubble = parsedDocument.getElementById('cart-icon-bubble');

    if (updatedBubble) {
      cartBubble.innerHTML = updatedBubble.innerHTML;
    }
  }

  renderProgressStepsFor(markersElement, labelsElement = null) {
    const maxPackSize = this.getMaxPackSize();
    const packSizes = this.getSortedPackSizes();

    if (markersElement) {
      markersElement.innerHTML = packSizes
        .map((packSize) => {
          const position = (packSize / maxPackSize) * 100;

          return `<span class="box-builder__progress-marker" data-progress-marker="${packSize}" style="left: ${position}%">${packSize}</span>`;
        })
        .join('');
    }

    if (labelsElement) {
      labelsElement.innerHTML = packSizes
        .map((packSize) => {
          const position = (packSize / maxPackSize) * 100;
          const discountPercentage = this.getDiscountPercentageForSelection(packSize, this.purchaseType);
          const label = discountPercentage ? `${discountPercentage}%` : '';

          return `<span class="box-builder__progress-label" style="left: ${position}%">${label}</span>`;
        })
        .join('');
    }
  }

  updateProgress(totalItems) {
    const maxPackSize = this.getMaxPackSize();
    const progressPercent = (Math.min(totalItems, maxPackSize) / maxPackSize) * 100;

    if (this.progressFill) {
      this.progressFill.style.width = `${progressPercent}%`;
    }

    if (this.mobileProgressFill) {
      this.mobileProgressFill.style.width = `${progressPercent}%`;
    }

    this.renderProgressStepsFor(this.progressMarkers);
    this.renderProgressStepsFor(this.mobileProgressMarkers, this.mobileProgressLabels);

    this.querySelectorAll('[data-progress-marker]').forEach((marker) => {
      const markerValue = Number(marker.dataset.progressMarker || 0);
      marker.classList.toggle('is-active', totalItems >= markerValue);
    });
  }

  buildCartFormData(selectedItems, lineItemProperties, cartDrawer) {
    const formData = new FormData();

    selectedItems.forEach((item, index) => {
      formData.append(`items[${index}][id]`, item.variantId);
      formData.append(`items[${index}][quantity]`, item.quantity);

      const selectedPlan = this.isSubscription() ? item.plans?.[this.selectedPlanName] : null;
      if (selectedPlan?.id) {
        formData.append(`items[${index}][selling_plan]`, selectedPlan.id);
      }

      Object.entries(lineItemProperties).forEach(([key, value]) => {
        formData.append(`items[${index}][properties][${key}]`, value);
      });
    });

    formData.append('sections', this.getCartSections(cartDrawer));
    formData.append('sections_url', window.location.pathname);

    return formData;
  }

  getCartSections(cartDrawer) {
    if (!cartDrawer || typeof cartDrawer.getSectionsToRender !== 'function') {
      return 'cart-icon-bubble';
    }

    return cartDrawer
      .getSectionsToRender()
      .map((section) => section.id)
      .join(',');
  }

  renderCartResponse(cartDrawer, data) {
    if (cartDrawer && data.sections) {
      cartDrawer.classList.remove('is-empty');
      cartDrawer.renderContents(data);
    } else if (data.sections?.['cart-icon-bubble']) {
      this.updateCartBubble(data.sections['cart-icon-bubble']);
    }
  }

  async addSelectionsToCart() {
    if (this.isAddingToCart) return;

    const selectedItems = this.getSelectedItems();
    const totalItems = this.getTotalSelectedCount();
    this.syncPackSizeWithSelection();
    this.updatePlanOptions(selectedItems);

    if (!selectedItems.length) {
      this.setFeedback('Add minimum 1 product to continue.', true);
      this.updateSubmitState(selectedItems);
      return;
    }

    if (totalItems < this.packSize) {
      this.setFeedback(`Select ${this.packSize} items before adding your box to cart.`, true);
      this.updateSubmitState(selectedItems);
      return;
    }

    this.isAddingToCart = true;
    this.submitProgressState = 'preparing';
    this.setFeedback('');
    this.updateSubmitState(selectedItems);

    const cartDrawer = document.querySelector('cart-drawer');
    const lineItemProperties = this.getLineItemProperties();
    const formData = this.buildCartFormData(selectedItems, lineItemProperties, cartDrawer);

    try {
      const discountCode = this.getDiscountCodeForBundle(lineItemProperties.bundle);

      if (discountCode) {
        this.submitProgressState = 'discount';
        this.updateSubmitState(selectedItems);
        await this.primeDiscountCode(discountCode);
      }

      this.submitProgressState = 'adding-items';
      this.updateSubmitState(selectedItems);
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: formData,
      });
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.description || 'Unable to add these products to cart.');
      }

      this.submitProgressState = 'updating-cart';
      this.updateSubmitState(selectedItems);
      this.renderCartResponse(cartDrawer, data);

      this.closeMobileSidebar();
      this.setFeedback('Your box has been added to cart.');
    } catch (error) {
      this.setFeedback(error.message || 'Unable to add these products to cart.', true);
    } finally {
      this.isAddingToCart = false;
      this.submitProgressState = 'idle';
      this.updateSubmitState();
    }
  }

  getSummaryItemMarkup(item) {
    const title = this.escapeAttribute(item.title);
    const imageMarkup = item.image
      ? `<img class="box-builder__summary-image" src="${this.escapeAttribute(item.image)}" alt="${title}" loading="lazy">`
      : '<span class="box-builder__summary-placeholder" aria-hidden="true"></span>';

    return `
      <li class="box-builder__summary-item">
        <button class="box-builder__summary-remove" type="button" data-remove-selected-item="${this.escapeAttribute(item.id)}" aria-label="Remove ${title}">
          <span aria-hidden="true">&times;</span>
        </button>
        <div class="box-builder__summary-link" aria-label="${title}">${imageMarkup}</div>
      </li>
    `;
  }

  getPlaceholderSummaryItemMarkup() {
    return `
      <li class="box-builder__summary-item box-builder__summary-item--placeholder">
        <div class="box-builder__summary-link box-builder__summary-link--placeholder" aria-hidden="true">
          <span class="box-builder__summary-placeholder"><span class="box-builder__summary-placeholder-label">Open slot</span></span>
        </div>
      </li>
    `;
  }

  renderSelectedItems(summaryItems, remainingSlots, shouldCollapseSummary) {
    if (!this.selectedItems) return;

    if (!shouldCollapseSummary) {
      this.isSummaryExpanded = false;
    }

    this.selectedItems.classList.toggle('is-collapsed', shouldCollapseSummary && !this.isSummaryExpanded);
    this.selectedItems.innerHTML =
      summaryItems.map((item) => this.getSummaryItemMarkup(item)).join('') +
      Array.from({ length: remainingSlots }, () => this.getPlaceholderSummaryItemMarkup()).join('');
  }

  renderSummary() {
    const items = Object.values(this.state);
    const selectedItems = this.getSelectedItems();
    const totalItems = this.getItemCount(items);
    this.syncPackSizeWithSelection();
    const remainingSlots = Math.max(this.packSize - totalItems, 0);
    const summaryItems = this.getExpandedSummaryItems(selectedItems);
    const renderedSummaryItemCount = summaryItems.length + remainingSlots;
    const shouldCollapseSummary = renderedSummaryItemCount > 3;

    this.syncPackSizeInputs();
    this.updatePackSizePromo(totalItems);

    const commonPlanNames = this.updatePlanOptions(selectedItems);
    this.updatePurchaseTypeSummary(selectedItems, commonPlanNames);
    const previewPlanName = this.getPreviewPlanName(commonPlanNames);
    const subscriptionSavingsPercentage = this.isSubscription()
      ? this.getSubscriptionSavingsPercentage(selectedItems, previewPlanName)
      : 0;

    const displayDiscountMapping = this.getCurrentDiscountMapping();
    const subtotalPrice = this.getSelectedTotal(selectedItems);
    const isDiscountEligible = totalItems >= this.packSize;
    const discountMapping = isDiscountEligible ? displayDiscountMapping : null;
    const discountAmount = isDiscountEligible
      ? this.getDiscountAmount(selectedItems, discountMapping, previewPlanName)
      : 0;
    const totalPrice = this.getCalculatedTotalPrice(selectedItems);

    this.setText(this.selectedCount, totalItems);
    this.setText(this.mobileSelectedCount, totalItems);
    this.setText(this.selectedTotal, this.formatMoney(totalPrice));
    this.setText(this.selectedTotalCompare, this.formatMoney(subtotalPrice));
    this.setHidden(this.selectedTotalCompare, !discountMapping || discountAmount <= 0);
    this.setText(this.mobileSelectedTotal, this.formatMoney(totalPrice));
    this.setText(this.discountSummaryLabel, subscriptionSavingsPercentage || displayDiscountMapping ? 'Discount' : '');

    if (this.discountSummaryAmount) {
      const discountPercentage = this.isSubscription()
        ? displayDiscountMapping
          ? this.getResolvedDiscountPercentage(displayDiscountMapping)
          : subscriptionSavingsPercentage
        : this.getDiscountSummaryPercentage(displayDiscountMapping);
      this.setText(this.discountSummaryAmount, discountPercentage ? `${discountPercentage}%` : '');
    }

    this.updateProgress(totalItems);

    if (this.emptyState) {
      this.setHidden(this.emptyState, items.length > 0);
    }

    if (this.sidebarInner) {
      this.sidebarInner.classList.toggle('is-empty-state', items.length === 0);
    }

    if (this.discountSummary) {
      this.discountSummary.hidden =
        items.length === 0 ||
        (this.isSubscription()
          ? !displayDiscountMapping && subscriptionSavingsPercentage <= 0
          : !displayDiscountMapping);
    }

    this.renderSelectedItems(summaryItems, remainingSlots, shouldCollapseSummary);

    if (this.selectedItemsToggle) {
      this.setHidden(this.selectedItemsToggle, !shouldCollapseSummary);
      this.setText(this.selectedItemsToggle, this.isSummaryExpanded ? 'Show less' : 'Show more');
      this.selectedItemsToggle.setAttribute('aria-expanded', this.isSummaryExpanded ? 'true' : 'false');
    }

    this.syncCards();
    this.updateCardPrices();
    this.updateSubmitState(selectedItems);
  }

  formatMoney(cents) {
    return new Intl.NumberFormat(document.documentElement.lang || 'en', {
      style: 'currency',
      currency: this.currencyCode,
    }).format(cents / 100);
  }

  escapeHtml(value) {
    const element = document.createElement('div');
    element.textContent = value;
    return element.innerHTML;
  }

  escapeAttribute(value) {
    return this.escapeHtml(value).replace(/"/g, '&quot;');
  }
}

if (!customElements.get('box-builder')) {
  customElements.define('box-builder', BoxBuilder);
}
